import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

const TERMS_VERSION = "2026-07-13";
const STORAGE_KEY = "jps_ack_version";

export const AcknowledgementGate = () => {
  const [visible, setVisible] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) !== TERMS_VERSION) {
      setVisible(true);
      document.body.style.overflow = "hidden";
    }
  }, []);

  const acknowledge = async () => {
    if (!agreed) return;
    setLoading(true);
    try {
      await api.post("/acknowledgements", { terms_version: TERMS_VERSION });
    } catch (err) {
      console.error("Acknowledgement record failed (continuing):", err);
    } finally {
      localStorage.setItem(STORAGE_KEY, TERMS_VERSION);
      document.body.style.overflow = "";
      setVisible(false);
    }
  };

  const leave = () => {
    window.location.href = "https://www.google.com";
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-ink/90 p-4" data-testid="ack-gate">
      <div className="w-full max-w-[620px] rounded-2xl bg-white shadow-2xl overflow-hidden fade-up">
        {/* Header */}
        <div className="flex items-center gap-6 px-8 py-6" style={{ background: "linear-gradient(100deg, #e9f7f0 0%, #e9f0f9 100%)" }}>
          <BrandLogo h={52} className="shrink-0" />
          <div>
            <p className="font-mono-label text-[11px] uppercase tracking-[0.2em] text-brand-blue">Registered Access</p>
            <h2 className="mt-1 font-display text-2xl sm:text-[26px] leading-tight text-brand-navy">A quick acknowledgement</h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-7 max-h-[45vh] overflow-y-auto">
          <p className="text-[15px] text-brand-navy/75 leading-relaxed">
            Welcome. Access here is limited to registered users, and everything listed is supplied{" "}
            <strong className="font-bold text-brand-navy">for laboratory research use only</strong> — these materials are
            not drugs, foods, or medical devices, and are not for human or veterinary consumption.
          </p>
          <p className="mt-4 text-[15px] text-brand-navy/75 leading-relaxed">
            We don't provide dosing, administration, or reconstitution guidance. By continuing, you confirm that:
          </p>
          <ul className="mt-4 space-y-2.5 list-disc pl-5 text-[15px] text-brand-navy/75 marker:text-brand-navy/40">
            <li>You are at least 21 years of age;</li>
            <li>You're acquiring these materials for legitimate <em>in vitro</em> laboratory research;</li>
            <li>You're qualified to handle research chemicals safely;</li>
            <li>You will not administer them to humans or animals;</li>
            <li>You'll comply with all applicable law.</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="bg-brand-mist border-t border-brand-navy/10 px-8 py-6">
          <div className="flex items-center gap-3">
            <input
              id="ack-agree"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="ack-checkbox"
              className="h-5 w-5 rounded border-brand-navy/30 text-brand-teal accent-[#29c39a] cursor-pointer"
            />
            <label htmlFor="ack-agree" className="text-[15px] font-medium text-brand-navy cursor-pointer select-none">I've read and agree to comply with these terms.</label>
          </div>

          <div className="mt-5 flex items-center gap-6">
            <button
              onClick={acknowledge}
              disabled={!agreed || loading}
              data-testid="ack-enter-button"
              className="rounded-full bg-brand-teal hover:bg-brand-blue disabled:opacity-40 disabled:hover:bg-brand-teal text-white font-semibold px-7 h-12 transition-colors"
            >
              {loading ? "Recording…" : "Acknowledge & enter"}
            </button>
            <button onClick={leave} data-testid="ack-leave-button" className="text-brand-navy/70 hover:text-brand-navy underline underline-offset-4 text-[15px]">
              Leave site
            </button>
          </div>

          <p className="mt-5 text-[12px] text-brand-navy/45">
            Terms version {TERMS_VERSION} · recorded with timestamp &amp; IP for your account
          </p>
        </div>
      </div>
    </div>
  );
};
